import type { IDataCompareData, IDataCompareHelper } from '@intelligence/v3/components/DataCompareBubble';
import { getFirstFiniteMetricValue, parseMetricNumber } from '@intelligence/v3/utils/metricCompareUtils';

interface IMetricComparePrevValueConfig {
  valueKey?: string;
  prevValueKeys?: string[];
  ratioValueKeys?: string[];
  diffValueKeys?: string[];
}

interface IBuildMetricCompareDataConfig {
  metric: any;
  granularity?: string;
  startDate?: string;
  endDate?: string;
  title?: string;
  icon?: string;
  entityType?: string;
  valueKey?: string;
  prevValueConfig?: IMetricComparePrevValueConfig;
  valueFormatter?: IDataCompareHelper['valueFormatter'];
}

const DEFAULT_PREV_VALUE_KEYS = ['prev_value', 'prevValue', 'link_value', 'linkValue'];

const DEFAULT_RATIO_VALUE_KEYS = ['growth_ratio'];

export const normalizeCompareGranularity = (granularity?: string): IGranularity => {
  switch ((granularity || '').toLowerCase()) {
    case 'hour':
    case 'hourly':
      return 'hourly';
    case 'day':
      return 'daily';
    case 'week':
      return 'weekly';
    case 'month':
      return 'monthly';
    default:
      return (granularity || '') as IGranularity;
  }
};

export const getMetricComparePrevValue = (metric: any, config: IMetricComparePrevValueConfig = {}): number => {
  const {
    valueKey = 'value',
    prevValueKeys = DEFAULT_PREV_VALUE_KEYS,
    ratioValueKeys = DEFAULT_RATIO_VALUE_KEYS,
    diffValueKeys = [],
  } = config;
  const prevValue = getFirstFiniteMetricValue(metric, prevValueKeys);

  if (Number.isFinite(prevValue)) {
    return prevValue;
  }

  const currentValue = parseMetricNumber(metric?.[valueKey]);

  if (!Number.isFinite(currentValue)) {
    return Number.NaN;
  }

  const ratioValue = getFirstFiniteMetricValue(metric, ratioValueKeys);

  if (Number.isFinite(ratioValue)) {
    const denominator = 1 + ratioValue;

    if (denominator !== 0) {
      return currentValue / denominator;
    }
  }

  const diffValue = getFirstFiniteMetricValue(metric, diffValueKeys);

  if (Number.isFinite(diffValue)) {
    return currentValue - diffValue;
  }

  return Number.NaN;
};

export const buildMetricCompareData = (
  config: IBuildMetricCompareDataConfig,
): (IDataCompareData & IDataCompareHelper) | undefined => {
  const {
    metric,
    granularity,
    startDate,
    endDate,
    title,
    icon,
    entityType,
    valueKey = 'value',
    prevValueConfig,
    valueFormatter,
  } = config;
  const value = parseMetricNumber(metric?.[valueKey]);
  const prevValue = getMetricComparePrevValue(metric, {
    valueKey,
    ...(prevValueConfig || {}),
  });

  if (!startDate || !endDate || !Number.isFinite(value) || !Number.isFinite(prevValue)) {
    return undefined;
  }

  const dataCompareData: IDataCompareData & IDataCompareHelper = {
    icon,
    title,
    granularity: normalizeCompareGranularity(granularity),
    startDate,
    endDate,
    value,
    prevValue,
    entityType: entityType || 'mobile',
  };

  if (valueFormatter) {
    dataCompareData.valueFormatter = valueFormatter;
  }

  return dataCompareData;
};
